
import { IconButton, Typography } from "@mui/material"
import ComboBox from "components/ComboBox"
import { renderUploadCell } from "components/FileUplaod/renderUploadCell"
import { truckFixCostColNm } from "./constant"
import { ArticleRounded, DeleteRounded } from "resource/muiImport"
import { numberFormat, dateFormat } from "utils"

export const truckFixCostColumns = [
  {
    field: 'ShowDetail',
    headerName: '',
    width: 40,
    align: 'center',
    renderCell: ({ id }) => id === 0 ? null : (
      <IconButton size="small">
        <ArticleRounded color="primary" />
      </IconButton>
    )
  },
  {
    field: "No",
    headerName: "No.",
    width: 50,
    align: 'center',
  },
  {
    field: "TukFixCostTypNm",
    headerName: truckFixCostColNm.TukFixCostTypId,
    width: 130,
  },
  {
    field: "StrDte",
    headerName: truckFixCostColNm.StrDte,
    width: 105,
    valueFormatter: ({ value }) => dateFormat(value),
  },
  {
    field: "EndDte",
    headerName: truckFixCostColNm.EndDte,
    width: 105,
    valueFormatter: ({ value }) => dateFormat(value),
  },
  {
    // Red once expired, orange inside 30 days — this screen doubles as the renewal worklist.
    field: "DayLeft",
    headerName: truckFixCostColNm.DayLeft,
    width: 80,
    align: "right",
    renderCell: ({ id, value }) => {
      if (id === 0 || value == null) return null
      return (
        <Typography variant="body2" sx={{
          color: value < 0 ? 'error.main' : value <= 30 ? 'warning.main' : 'text.primary',
          fontWeight: value <= 30 ? 'bold' : 'normal',
        }}>
          {value < 0 ? `หมดแล้ว ${-value}` : value}
        </Typography>
      )
    }
  },
  {
    field: "NumVeh",
    headerName: truckFixCostColNm.NumVeh,
    width: 70,
    align: "center",
  },
  {
    // The whole point of the header/detail split: one policy line, N vehicles.
    field: "VehNms",
    headerName: truckFixCostColNm.VehNms,
    flex: 1,
    minWidth: 160,
  },
  {
    field: "Amnt",
    headerName: truckFixCostColNm.Amnt,
    width: 120,
    align: "right",
    type: "number",
    valueGetter: ({ value }) => value == null ? null : +value,
    valueFormatter: ({ value }) => value == null ? "-" : numberFormat(value),
  },
  {
    // Highlighted when the items do not add up to the header — the control-total invariant.
    field: "ItmAmnt",
    headerName: truckFixCostColNm.ItmAmnt,
    width: 120,
    align: "right",
    type: "number",
    valueGetter: ({ value }) => value == null ? null : +value,
    valueFormatter: ({ value }) => value == null ? "-" : numberFormat(value),
    cellClassName: ({ id, row }) =>
      id === 0 || row.ItmAmnt == null ? '' : +row.ItmAmnt === +row.Amnt ? '' : 'amnt-mismatch',
  },
  {
    field: "MonthAmnt",
    headerName: truckFixCostColNm.MonthAmnt,
    width: 120,
    align: "right",
    type: "number",
    valueGetter: ({ value }) => value == null ? null : +value,
    valueFormatter: ({ value }) => value == null ? "-" : numberFormat(value),
    cellClassName: "month-amnt-color",
  },
  {
    field: "SupSNm",
    headerName: "ผู้ขาย/ประกัน",
    width: 130,
  },
  {
    // From the TYPE, not the document — the GL account belongs to the document type.
    field: "AccCd",
    headerName: truckFixCostColNm.CoAId,
    width: 140,
    valueGetter: ({ row }) => row.AccCd ? `${row.AccCd} ${row.AccNm ?? ''}`.trim() : null,
  },
  {
    field: "DocNo",
    headerName: truckFixCostColNm.DocNo,
    width: 120,
  },
  {
    field: "NumFile",
    headerName: "ไฟล์",
    width: 60,
    align: "center",
  },
  {
    field: "Rmk",
    headerName: truckFixCostColNm.Rmk,
    width: 140,
  },
]

/**
 * Rows in ReadTruckFixCostFileDialog — one line per VEHICLE read off the uploaded
 * documents, so a fleet policy naming 42 ทะเบียน shows 42 lines under one file name and
 * a stack of 26 ป้ายวงกลม shows 26 lines with 26 file names.
 *
 * Everything the operator may have to correct before the draft is used is editable here:
 * the vehicle (a picker, not free text — a plate only matters once it resolves to a
 * TukId/TlrId), the per-vehicle เลขที่, the period and the amount. Period is a property of
 * the FILE, so editing it on one line moves every line read off that same file.
 *
 * A factory because the picker needs the fleet and the dialog's commit handler.
 */
export const readTruckFixCostColumns = ({ vehCombo, onPickVehicle }) => [
  {
    field: "No",
    headerName: "No.",
    width: 50,
    align: 'center',
  },
  {
    field: "FileNm",
    headerName: "ไฟล์",
    flex: 1,
    minWidth: 140,
  },
  {
    field: "TypNm",
    headerName: truckFixCostColNm.TukFixCostTypId,
    width: 110,
    // A type name Gemini read but the branch has no row for cannot be saved as-is.
    renderCell: ({ row }) => row.error ? (
      <Typography variant="body2" color="error.main">{row.error}</Typography>
    ) : (
      <Typography variant="body2" color={row.TukFixCostTypId ? 'text.primary' : 'error.main'}>
        {row.TypNm || '-'}
      </Typography>
    )
  },
  {
    field: "LicNm",
    headerName: "ทะเบียน",
    width: 210,
    // The label carries what the paper said, so a wrong read stays visible while it is
    // being corrected; red until the line points at a real vehicle.
    renderCell: ({ row }) => row.iIdx < 0 ? null : (
      <ComboBox
        options={vehCombo || []}
        label={row.VehKey ? "ทะเบียน" : row.LicNm ? `ไม่พบ ${row.LicNm}` : "เลือกรถ"}
        selectedId={row.VehKey || null}
        setSelectedId={(id) => onPickVehicle(row.id, id)}
        sx={{ width: 190 }}
      />
    )
  },
  {
    field: "DocNo",
    headerName: truckFixCostColNm.ItemDocNo,
    width: 150,
    editable: true,
  },
  {
    field: "StrDte",
    headerName: truckFixCostColNm.StrDte,
    width: 115,
    type: 'date',
    editable: true,
    valueGetter: ({ value }) => value ? new Date(value) : null,
    valueFormatter: ({ value }) => dateFormat(value),
  },
  {
    field: "EndDte",
    headerName: truckFixCostColNm.EndDte,
    width: 115,
    type: 'date',
    editable: true,
    valueGetter: ({ value }) => value ? new Date(value) : null,
    valueFormatter: ({ value }) => dateFormat(value),
  },
  {
    field: "Amnt",
    headerName: truckFixCostColNm.ItemAmnt,
    width: 110,
    align: 'right',
    headerAlign: 'right',
    type: 'number',
    editable: true,
    valueFormatter: ({ value }) => value == null ? '-' : numberFormat(value),
  },
]

/**
 * The vehicles covered by one document, inside TruckFixCostDialog.
 *
 * A factory rather than a constant because three cells need the dialog's own handlers:
 * the vehicle picker for a plate that matched nothing, the per-vehicle certificate, and
 * the delete button. `DocNo` and `Amnt` are edited in place (onCellEditCommit).
 */
export const truckFixCostItemColumns = ({
  truckCombo, trailerCombo, onPickTruck, onDelete, handleDeleteFile, onFileUploadSuccess,
  onDeferFile,
}) => [
  {
    field: "No",
    headerName: "No.",
    width: 50,
    align: 'center',
  },
  {
    field: "Veh",
    headerName: "รถ / หางพ่วง",
    width: 210,
    // A plate Gemini read but could not resolve leaves the line without an id, and the
    // save is rejected without one — so the picker sits where the failure shows.
    // id 0 is the pinned รวม row: label only, no controls and nothing to resolve.
    renderCell: ({ row }) => row.id === 0 ? (
      <Typography variant="body2" fontWeight="bold">{row.Veh}</Typography>
    ) : (row.TukId || row.TlrId) ? (
      <Typography variant="body2">
        {row.TukCd || row.TukLicNm || row.TlrLicNm
          || truckCombo?.find(t => t.id === row.TukId)?.label
          || trailerCombo?.find(t => t.id === row.TlrId)?.label
          || '-'}
      </Typography>
    ) : (
      <ComboBox
        options={truckCombo || []}
        label={row.LicNm ? `ไม่พบ ${row.LicNm}` : "เลือกรถ"}
        selectedId={null}
        setSelectedId={(id) => onPickTruck(row.id, id)}
        sx={{ width: 190 }}
      />
    )
  },
  {
    // ป้ายวงกลม / พ.ร.บ. เลขที่ of THIS vehicle. Blank for a shared policy, whose number
    // is the header DocNo.
    field: "DocNo",
    headerName: truckFixCostColNm.ItemDocNo,
    width: 170,
    editable: true,
  },
  {
    field: "Amnt",
    headerName: truckFixCostColNm.ItemAmnt,
    width: 130,
    align: 'right',
    headerAlign: 'right',
    type: 'number',
    editable: true,
    valueFormatter: ({ value }) => value == null ? '-' : numberFormat(value),
  },
  {
    // Derived, never stored: item.Amnt spread over the months the header covers, which is
    // exactly what AMORT_SQL gives the report. Read-only — edit the total, not the slice.
    field: "MonthAmnt",
    headerName: truckFixCostColNm.MonthAmnt,
    width: 110,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: ({ value }) => value == null ? '-' : numberFormat(value),
  },
  {
    // ป้ายวงกลม / พ.ร.บ. of THIS vehicle: drop a file straight onto the row, and see or
    // delete what is already attached. The grid is keyed by index, so the upload id comes
    // off the row; a line not yet saved has none, so its file is HELD and uploaded after
    // บันทึก — the same path the AI read already uses.
    field: "Files",
    headerName: "ไฟล์",
    width: 120,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: renderUploadCell({
      uploadApiNm: "/truck-fix-cost/uploadTruckFixCostItemFile",
      formDataKey: "TukFixCostItmId",
      getRefId: ({ row }) => row.TukFixCostItmId,
      handleDeleteFile,
      onFileUploadSuccess,
      onDeferFile,
      getPendingFile: ({ row }) => row._pendingFile,
    }),
  },
  {
    field: "Del",
    headerName: "",
    width: 50,
    align: 'center',
    sortable: false,
    renderCell: ({ row }) => row.id === 0 ? null : (
      <IconButton size="small" onClick={() => onDelete(row.id)}>
        <DeleteRounded fontSize="small" color="action" />
      </IconButton>
    )
  },
]
